import CommentsDisabledIcon from '@mui/icons-material/CommentsDisabled';
import CommentsDisabledOutlinedIcon from '@mui/icons-material/CommentsDisabledOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import MessageIcon from '@mui/icons-material/Message';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import { useClinics } from '../../../hooks/useClinics';
import useDebounce from '../../../hooks/useDebounce';
import {
  FindAllMembersParams,
  MemberWithCommunicationStatus,
  UpdateMemberClinicPayload,
} from '../../../interfaces/appointments';
import { Pagination } from '../../../interfaces/common';
import { InternalCoreUser } from '../../../interfaces/core.adapter';
import messagesService from '../../../services/appointments/messages';
import coreService from '../../../services/core';
import { DEFAULT_PAGE_SIZE, INITIAL_PAGINATION_STATE } from '../../../util/constants';
import { CustomTable } from '../../common/CustomTable';
import { ClinicFilter } from '../ClinicFilter';
import '../communication-center.css';

type Props = {
  breadcrumbs: React.ReactNode;
};

export const MembersList = ({ breadcrumbs }: Props) => {
  const [communicationMemberStatuses, setCommunicationMemberStatuses] = useState<
    MemberWithCommunicationStatus[]
  >([]);
  const [page, setPage] = useState<number>(1);
  const [filters, setFilters] = useState<FindAllMembersParams>({});
  const [pagination, setPagination] = useState<Pagination>(INITIAL_PAGINATION_STATE);
  const [coreMembers, setCoreMembers] = useState<InternalCoreUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<number | null>(null);
  const [unreachableModalOpen, setUnreachableModalOpen] = useState<boolean>(false);
  const [unreachableReason, setUnreachableReason] = useState<string>('');
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [availabilityFilter, setAvailabilityFilter] = useState<string>('all');
  const [tooltipDetails, setTooltipDetails] = useState<MemberWithCommunicationStatus | null>(null);
  const [clinicModalMember, setClinicModalMember] = useState<MemberWithCommunicationStatus | null>(
    null,
  );
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState<boolean>(false);
  const isMountedRef = useRef<boolean>(true);

  const notify = useNotify();
  const { clinics } = useClinics({ notifyOnError: false });

  const isMenuOpen = (rowId: number) => menuRowId === rowId;

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, rowId: number) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };

  const fetchMemberCommunicationStatuses = useCallback(
    async (filters?: FindAllMembersParams) => {
      setLoading(true);
      const response = await messagesService.findAllMembers({
        ...(filters &&
          Object.entries(filters).reduce(
            (prev, [filter, value]) => ({ ...prev, ...(value && { [filter]: value }) }),
            {},
          )),
        page,
        pageSize: DEFAULT_PAGE_SIZE,
      });

      if (!isMountedRef.current) {
        return;
      }

      if (!response?.data || !response.pagination) {
        notify(response.message || 'There is a problem fetching members');
        return;
      }

      setPagination(response.pagination);
      setCommunicationMemberStatuses(response.data);
      setLoading(false);
    },
    [notify, page],
  );

  const updateMember = async ({
    coreMemberId,
    emailOptedOut,
    smsOptedOut,
  }: {
    coreMemberId: number;
    emailOptedOut?: boolean;
    smsOptedOut?: boolean;
  }) => {
    setLoading(true);

    const response = await messagesService.updateMemberOptedOutMethods({
      coreMemberId,
      ...(emailOptedOut !== undefined && { emailOptedOutStatus: emailOptedOut }),
      ...(smsOptedOut !== undefined && { smsOptedOutStatus: smsOptedOut }),
    });

    if (!response?.data) {
      notify(response.message || 'There is a problem updating this particular member');
      return;
    }

    await fetchMemberCommunicationStatuses(filters);
    setLoading(false);
  };

  const updateMemberUnreachableStatus = async ({
    coreMemberId,
    isUnreachable,
    unreachableReason,
  }: {
    coreMemberId: number;
    isUnreachable: boolean;
    unreachableReason?: string;
  }) => {
    setLoading(true);

    const response = await messagesService.updateMemberUnreachableStatus({
      coreMemberId,
      isUnreachable,
      unreachableReason,
    });

    if (!response?.data) {
      notify(response.message || 'There is a problem updating this particular member');
      return;
    }

    await fetchMemberCommunicationStatuses(filters);
    setLoading(false);
  };

  const repairMemberClinic = async (payload: UpdateMemberClinicPayload) => {
    setLoading(true);

    const response = await messagesService.updateMemberClinic(payload);

    if (!response?.data) {
      notify(response.message || 'There is a problem updating this member clinic');
      setLoading(false);
      return;
    }

    const { messages, calls, voicemails } = response.data.affectedCounts;
    notify(
      `Clinic updated — ${messages} messages, ${calls} calls, ${voicemails} voicemails repaired`,
      { type: 'success' },
    );

    handleClinicModalClose();
    await fetchMemberCommunicationStatuses(filters);
    setLoading(false);
  };

  const triggerClinicSync = async () => {
    setSyncConfirmOpen(false);
    setLoading(true);

    const response = await messagesService.triggerClinicSync({ scope: 'drifted' });

    if (!response?.data) {
      notify(response.message || 'There is a problem starting the clinic sync');
      setLoading(false);
      return;
    }

    notify('Clinic sync started — records will update in the background shortly', { type: 'info' });
    setLoading(false);
  };

  const handleOpenClinicModal = (member: MemberWithCommunicationStatus) => {
    setClinicModalMember(member);
    setSelectedClinicId(null);
    handleCloseMenu();
  };

  const handleClinicModalClose = () => {
    setClinicModalMember(null);
    setSelectedClinicId(null);
  };

  const handleSetUnreachable = (memberId: number) => {
    setSelectedMemberId(memberId);
    setUnreachableModalOpen(true);
    handleCloseMenu();
  };

  const handleUnreachableModalClose = () => {
    setUnreachableModalOpen(false);
    setUnreachableReason('');
    setSelectedMemberId(null);
  };

  const handleUnreachableModalConfirm = async () => {
    if (selectedMemberId) {
      await updateMemberUnreachableStatus({
        coreMemberId: selectedMemberId,
        isUnreachable: true,
        unreachableReason: unreachableReason || undefined,
      });
      handleUnreachableModalClose();
    }
  };

  const searchUsersByTerm = async ({ searchTerm }: { searchTerm?: string | null }) => {
    if (!searchTerm) {
      return [];
    }

    return (await coreService.fetchMembers({ searchTerm, target: 'members' })).data;
  };

  const handleFilterChanges = (filter?: {
    [filter: string]: boolean | number | string | undefined | string[] | number[];
  }) => {
    const updatedFilters = {
      ...filters,
      ...filter,
    };
    setFilters(updatedFilters);
    debouncedFetchMemberCommunicationStatuses(updatedFilters);
  };

  const debouncedFetchMembers = useDebounce(async (param: { searchTerm: string }) => {
    setCoreMembers(await searchUsersByTerm({ ...param }));
  });

  const debouncedFetchMemberCommunicationStatuses = useDebounce(fetchMemberCommunicationStatuses);

  useEffect(() => {
    isMountedRef.current = true;
    (async () => await fetchMemberCommunicationStatuses(filters))();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchMemberCommunicationStatuses, filters]);

  return (
    <>
      <Box className='tab-section members-list-center-section' marginTop={0}>
        <Box className='members-list-center-header'>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            marginBottom='5px'>
            {breadcrumbs}
            <Tooltip title='Reconcile members whose clinic is unassigned or out of date with Core, in the background'>
              <Button
                size='small'
                variant='outlined'
                startIcon={<SyncIcon />}
                onClick={() => setSyncConfirmOpen(true)}>
                Sync from Core
              </Button>
            </Tooltip>
          </Box>

          <Box display='flex' alignItems='flex-start' gap={1} mt={2} mb={5} flexWrap='wrap'>
            <Box display='flex' flexDirection='column' alignItems='flex-start'>
              <Typography
                sx={{
                  '&.MuiTypography-root': { fontSize: '13px' },
                }}>
                Search Patient
              </Typography>
              <Autocomplete
                fullWidth
                className='member-selector'
                options={coreMembers}
                filterOptions={(x) => x}
                getOptionLabel={(option) => `${option.fullName} ID: ${option.id}`}
                onChange={(e, v) => handleFilterChanges({ coreMemberId: v?.id || '' })}
                noOptionsText='No members found'
                value={coreMembers.find((m) => m.id === Number(filters.coreMemberId)) || undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    placeholder='Enter a member name'
                    onChange={(_) => debouncedFetchMembers({ searchTerm: _.target.value })}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Avatar
                            sx={{
                              color: 'action.active',
                              mr: 1,
                            }}
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component='li' {...props}>
                    <Avatar sx={{ mr: 2, bgcolor: '#007bff' }}>
                      {`${option.fullName?.[0]}${option.fullName?.[1]}`.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography fontSize={'0.8rem'}>{option.fullName}</Typography>
                      <Typography variant='caption' color='textSecondary'>
                        ID: {option.id}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Box>

            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              sx={{
                width: '150px',
              }}>
              <Typography
                sx={{
                  '&.MuiTypography-root': { fontSize: '13px' },
                }}>
                Availability
              </Typography>
              <Select
                id='availability-select'
                value={availabilityFilter}
                onChange={(event: SelectChangeEvent) => {
                  const value = event.target.value;
                  setAvailabilityFilter(value);

                  // Update filters based on selection
                  if (value === 'all') {
                    handleFilterChanges({ unreachableStatus: [] });
                  } else if (value === 'unreachable') {
                    handleFilterChanges({ unreachableStatus: ['unreachable'] });
                  } else if (value === 'reachable') {
                    handleFilterChanges({ unreachableStatus: ['reachable'] });
                  }
                }}
                size='small'
                fullWidth
                sx={{
                  mt: 0,
                  '& .MuiSelect-select': {
                    fontSize: '13px',
                    py: 1,
                  },
                }}>
                <MenuItem value='all'>All</MenuItem>
                <MenuItem value='unreachable'>Unreachable</MenuItem>
                <MenuItem value='reachable'>Reachable</MenuItem>
              </Select>
            </Box>

            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              sx={{
                width: '100px',
              }}>
              <Typography
                sx={{
                  '&.MuiTypography-root': { fontSize: '13px' },
                }}>
                Opted-out by
              </Typography>

              <Box sx={{ display: 'flex', ml: 1, span: { fontSize: '13px' } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.optedOutMethods?.includes('sms') || false}
                      onChange={() => {
                        const newOptedOut = filters.optedOutMethods?.includes('sms')
                          ? filters.optedOutMethods.filter((item) => item !== 'sms')
                          : [...(filters.optedOutMethods || []), 'sms'];
                        handleFilterChanges({ optedOutMethods: newOptedOut });
                      }}
                      name='sms'
                      color='primary'
                      size='small'
                    />
                  }
                  label='SMS'
                />

                {/* Email filter commented out as requested */}
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.optedOutMethods?.includes('email') || false}
                      onChange={(event) => {
                        const newOptedOut = filters.optedOutMethods?.includes('email')
                          ? filters.optedOutMethods.filter((item) => item !== 'email')
                          : [...(filters.optedOutMethods || []), 'email'];
                        handleFilterChanges({ optedOutMethods: newOptedOut });
                      }}
                      name='email'
                      color='primary'
                      size='small'
                    />
                  }
                  label='Email'
                /> */}
              </Box>
            </Box>

            <ClinicFilter
              selectedClinicIds={filters.clinicIds || []}
              includeUnassigned={!!filters.includeUnassigned}
              onChange={({ clinicIds, includeUnassigned }) =>
                handleFilterChanges({ clinicIds, includeUnassigned })
              }
            />
          </Box>
        </Box>

        <CustomTable
          columns={[
            { key: 'availability', label: 'Availability', width: '50px', align: 'center' },
            { key: 'name', label: 'Name', width: '100px' },
            { key: 'clinic', label: 'Clinic', width: '100px' },
            { key: 'email', label: 'Email', width: '130px' },
            { key: 'phone', label: 'Phone', width: '80px' },
            { key: 'status', label: 'Status', width: '60px' },
            { key: 'actions', label: 'Actions', width: '30px', align: 'right' },
          ]}
          defaultText='No registered members'
          key={'communications-center-members-list-table'}
          loading={loading}
          rows={communicationMemberStatuses.map((data) => {
            return {
              availability: (
                <Box
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  onMouseEnter={() => (data.isUnreachable ? setTooltipDetails(data) : {})}
                  onMouseLeave={() => setTooltipDetails(null)}>
                  {/* Custom tooltip for unreachable members */}
                  {tooltipDetails && tooltipDetails.id === data.id && data.isUnreachable && (
                    <Box
                      sx={{
                        position: 'absolute',
                        zIndex: 100,
                        backgroundColor: 'white',
                        translate: '-85px -75px',
                        padding: '15px',
                        borderRadius: '5px',
                        boxShadow: '0 0 5px rgba(0, 0, 0, 0.2)',
                        minWidth: '200px',
                        border: '1px solid #e0e0e0',
                      }}>
                      <Box display='flex' flexDirection='column' gap={1}>
                        <Box display='flex' flexDirection='column' alignItems='flex-start' gap={0}>
                          <Typography fontWeight='bold' fontSize='0.8rem' color='#d32f2f'>
                            Status: Unreachable
                          </Typography>
                        </Box>

                        <Box display='flex' flexDirection='column' alignItems='flex-start' gap={0}>
                          <Typography fontWeight='bold' fontSize='0.8rem'>
                            Member:
                          </Typography>
                          <Typography fontSize='0.75rem' color='textSecondary'>
                            {data.member.firstName} {data.member.lastName}
                          </Typography>
                        </Box>

                        <Box display='flex' flexDirection='column' alignItems='flex-start' gap={0}>
                          <Typography fontWeight='bold' fontSize='0.8rem'>
                            ID:
                          </Typography>
                          <Typography fontSize='0.75rem' color='textSecondary'>
                            {data.member.id}
                          </Typography>
                        </Box>

                        {data.unreachableReason && (
                          <Box
                            display='flex'
                            flexDirection='column'
                            alignItems='flex-start'
                            gap={0}>
                            <Typography fontWeight='bold' fontSize='0.8rem'>
                              Reason:
                            </Typography>
                            <Typography fontSize='0.75rem' color='textSecondary'>
                              {data.unreachableReason}
                            </Typography>
                          </Box>
                        )}

                        <Box display='flex' flexDirection='column' alignItems='flex-start' gap={0}>
                          <Typography fontWeight='bold' fontSize='0.8rem'>
                            Contact Info:
                          </Typography>
                          <Typography fontSize='0.75rem' color='textSecondary'>
                            {data.member.email}
                          </Typography>
                          <Typography fontSize='0.75rem' color='textSecondary'>
                            {data.member.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Fallback to simple tooltip for reachable members */}
                  {data.isUnreachable ? (
                    <Box
                      sx={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: '#d32f2f',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <Tooltip title='Reachable'>
                      <Box
                        sx={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: '#0EC917',
                          cursor: 'pointer',
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              ),
              name: (
                <Box flexDirection='column' display='flex'>
                  <p style={{ margin: 0 }}>{`${data.member.firstName} ${data.member.lastName}`}</p>
                  <p style={{ margin: 0, color: '#8f8f8f' }}>ID: {data.member.id}</p>
                </Box>
              ),
              clinic: <p>{data.member.clinic.name}</p>,
              email: <p>{data.member.email}</p>,
              phone: <p>{data.member.phone}</p>,
              status: (
                <Box display='flex' alignItems='center'>
                  <Tooltip title={data.smsOptedOut ? 'SMS disabled' : 'SMS enabled'}>
                    <Box
                      sx={{
                        display: 'flex',
                        padding: '5px',
                        color: 'white',
                        backgroundColor: data.smsOptedOut ? 'white' : '#1A667A',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        '& svg': {
                          width: '20px',
                          height: '20px',
                        },
                      }}>
                      {data.smsOptedOut ? (
                        <CommentsDisabledOutlinedIcon color='error' />
                      ) : (
                        <MessageIcon />
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              ),
              actions: (
                <Box>
                  <IconButton onClick={(e) => handleOpenMenu(e, data.id)} size='small'>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    open={isMenuOpen(data.id)}
                    anchorEl={menuAnchorEl}
                    onClose={handleCloseMenu}>
                    {/* Email actions hidden as requested */}
                    {/* <MenuItem
                      onClick={() => {
                        updateMember({
                          coreMemberId: data.member.id,
                          emailOptedOut: !data.emailOptedOut,
                        });
                        handleCloseMenu();
                      }}>
                      {data.emailOptedOut ? (
                        <>
                          <ListItemIcon>
                            <EmailIcon fontSize='small' />
                          </ListItemIcon>
                          <ListItemText>Enable email messages</ListItemText>
                        </>
                      ) : (
                        <>
                          <ListItemIcon>
                            <UnsubscribeIcon fontSize='small' color='error' />
                          </ListItemIcon>
                          <ListItemText>Disable email messages</ListItemText>
                        </>
                      )}
                    </MenuItem> */}
                    <MenuItem
                      onClick={() => {
                        updateMember({
                          coreMemberId: data.member.id,
                          smsOptedOut: !data.smsOptedOut,
                        });
                        handleCloseMenu();
                      }}>
                      {data.smsOptedOut ? (
                        <>
                          <ListItemIcon>
                            <MessageIcon fontSize='small' />
                          </ListItemIcon>
                          <ListItemText>Enable SMS messages</ListItemText>
                        </>
                      ) : (
                        <>
                          <ListItemIcon>
                            <CommentsDisabledIcon fontSize='small' color='error' />
                          </ListItemIcon>
                          <ListItemText>Disable SMS messages</ListItemText>
                        </>
                      )}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        if (data.isUnreachable) {
                          updateMemberUnreachableStatus({
                            coreMemberId: data.member.id,
                            isUnreachable: false,
                          });
                          handleCloseMenu();
                        } else {
                          handleSetUnreachable(data.member.id);
                        }
                      }}>
                      {data.isUnreachable ? (
                        <>
                          <ListItemIcon>
                            <PhonelinkEraseIcon fontSize='small' />
                          </ListItemIcon>
                          <ListItemText>Set as reachable</ListItemText>
                        </>
                      ) : (
                        <>
                          <ListItemIcon>
                            <PhonelinkEraseIcon fontSize='small' color='error' />
                          </ListItemIcon>
                          <ListItemText>Set as unreachable</ListItemText>
                        </>
                      )}
                    </MenuItem>
                    <MenuItem onClick={() => handleOpenClinicModal(data)}>
                      <ListItemIcon>
                        <LocalHospitalOutlinedIcon fontSize='small' />
                      </ListItemIcon>
                      <ListItemText>Assign / repair clinic</ListItemText>
                    </MenuItem>
                  </Menu>
                </Box>
              ),
            };
          })}
          pagination={{
            rowsPerPageOptions: [DEFAULT_PAGE_SIZE],
            rowPerPage: pagination.perPage,
            page: pagination.currentPage,
            totalCount: pagination.totalCount,
            onPageChange: (page: number) => setPage(page),
          }}
        />
      </Box>

      {/* Unreachable Modal */}
      <Dialog
        open={unreachableModalOpen}
        onClose={handleUnreachableModalClose}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>Confirm member availability update</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
            (Optional) Please provide a reason for marking this member as temporary unreachable:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            variant='outlined'
            value={unreachableReason}
            onChange={(e) => setUnreachableReason(e.target.value)}
            placeholder='e.g., Phone number is no longer available, Landline number, etc.'
          />
          <Typography variant='body2' sx={{ opacity: 0.8, mb: 2 }}>
            *This action can be reversed afterwards
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnreachableModalClose} color='secondary'>
            Cancel
          </Button>
          <Button onClick={handleUnreachableModalConfirm} color='primary' variant='contained'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign / repair clinic modal */}
      <Dialog open={!!clinicModalMember} onClose={handleClinicModalClose} maxWidth='sm' fullWidth>
        <DialogTitle>Assign / repair clinic</DialogTitle>
        {clinicModalMember && (
          <DialogContent>
            <Typography variant='body2' sx={{ mb: 2 }}>
              {clinicModalMember.member.firstName} {clinicModalMember.member.lastName} (ID:{' '}
              {clinicModalMember.member.id})
            </Typography>

            <Box display='flex' gap={2} sx={{ mb: 2 }}>
              <Box flex={1}>
                <Typography variant='caption' color='textSecondary'>
                  Comms clinic (current)
                </Typography>
                <Box display='flex' alignItems='center' gap={1}>
                  <Typography variant='body2'>
                    {clinicModalMember.coreMemberClinicName || 'Unassigned'}
                  </Typography>
                  {clinicModalMember.clinicSource === 'MANUAL' && (
                    <Chip label='manual' size='small' color='info' variant='outlined' />
                  )}
                </Box>
              </Box>
              <Box flex={1}>
                <Typography variant='caption' color='textSecondary'>
                  Core clinic
                </Typography>
                <Typography variant='body2'>
                  {clinicModalMember.member.clinic.name || '—'}
                </Typography>
              </Box>
            </Box>

            {String(clinicModalMember.member.clinic.id ?? '') !==
              String(clinicModalMember.coreMemberClinicId ?? '') && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                The comms clinic is out of sync with Core. "Sync from Core" will repair it.
              </Alert>
            )}

            <Button
              fullWidth
              variant='contained'
              startIcon={<SyncIcon />}
              sx={{ mb: 3 }}
              onClick={() =>
                repairMemberClinic({
                  coreMemberId: clinicModalMember.member.id,
                  mode: 'fromCore',
                })
              }>
              Sync from Core
            </Button>

            <Divider sx={{ mb: 2 }}>or assign manually</Divider>

            <Autocomplete
              size='small'
              options={clinics}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, selected) => option.id === selected.id}
              value={clinics.find((clinic) => clinic.id === selectedClinicId) ?? null}
              onChange={(_event, selected) => setSelectedClinicId(selected?.id ?? null)}
              renderInput={(params) => (
                <TextField {...params} placeholder='Select a clinic' variant='outlined' />
              )}
            />

            <Typography variant='body2' sx={{ opacity: 0.8, mt: 2 }}>
              Updates this member's calls, messages, and voicemails. A manual choice is kept and
              won't be overwritten by the automatic sync.
            </Typography>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleClinicModalClose} color='secondary'>
            Cancel
          </Button>
          <Button
            color='primary'
            variant='contained'
            disabled={!selectedClinicId}
            onClick={() => {
              const clinic = clinics.find((item) => item.id === selectedClinicId);
              if (clinicModalMember && clinic) {
                repairMemberClinic({
                  coreMemberId: clinicModalMember.member.id,
                  mode: 'manual',
                  clinicId: clinic.id,
                  clinicName: clinic.name,
                });
              }
            }}>
            Assign manually
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync-from-Core confirmation */}
      <Dialog open={syncConfirmOpen} onClose={() => setSyncConfirmOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Sync clinics from Core?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='textSecondary'>
            This reconciles members whose clinic is unassigned or out of date with Core and repairs
            their communications records. It runs in the background and may take a few minutes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncConfirmOpen(false)} color='secondary'>
            Cancel
          </Button>
          <Button onClick={triggerClinicSync} color='primary' variant='contained'>
            Start sync
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
